import { ActionHandler } from "..";
import {
  getEligibleMedProducts,
  getEligibleMedProductsPlanSelection,
  MED_FLOW,
} from "src/utils/medEligibility";
import {
  getHRTRecommendedPlanTier,
  getMedRecommendedPlanTier,
} from "src/components/med/planInformation/utils";
import {
  setPlansInRecommendationContext,
  setRecommendationContext,
} from "src/utils/redux/slices/recommendationContext";
import {
  queryRecommendedBuyflowOffers,
  RecommendedOffersResult,
} from "src/api/recommendedOffers";
import { captureException } from "src/utils/error";
import { trackEvent } from "src/utils/api/tracker";
import {
  shouldUseOffersRecommendation,
  isOpenToMedRecommendation,
  shouldCallRecommenderOffersApi,
  getForcedMedRecommendationSku,
} from "src/utils/userSegment/features";
import { queryBuyflowEligibleOffers } from "src/utils/api/purchase/eligibleOffers";
import { trackMedEligibilityCheckpoint } from "src/utils/medEligibilityTracking";
import { getMedSKUByProductName } from "src/utils/plans";
import { SaleItemInfo } from "src/utils/redux/slices/checkoutOrders";

// Module-level promise to track the pending GraphQL query
let pendingRecommendationPromise: Promise<RecommendedOffersResult | null> | null =
  null;

/**
 * Initiates loading of recommended med plans without blocking.
 * The actual GraphQL query runs asynchronously to avoid blocking page animations.
 */
export const loadRecommendedMedPlans: ActionHandler = ({ params }) => {
  const { flow } = params;
  const openToMedRecommendation = isOpenToMedRecommendation();
  const eligibleMedProducts = getEligibleMedProducts();

  let recommendedMedTier =
    flow === MED_FLOW.HRT
      ? getHRTRecommendedPlanTier()
      : getMedRecommendedPlanTier(eligibleMedProducts);

  const forcedRecommendationSku = getForcedMedRecommendationSku();
  if (
    openToMedRecommendation &&
    forcedRecommendationSku &&
    eligibleMedProducts.includes(forcedRecommendationSku)
  ) {
    recommendedMedTier = forcedRecommendationSku;
  }

  const eligibleMedTiers = getEligibleMedProductsPlanSelection(
    recommendedMedTier,
    eligibleMedProducts
  );

  trackEvent("RecommenderBuyflowDefaultOffers", {
    eligibleSKUs: eligibleMedTiers,
    recommendedMedTier,
  });

  trackMedEligibilityCheckpoint("plan_load", {
    recommendedMedTier,
    eligibleMedTiers,
  });

  // Set initial recommendation context with default values
  setPlansInRecommendationContext(recommendedMedTier, eligibleMedTiers, flow);

  // Try to get recommendations from recommender API asynchronously
  // Store the promise so we can await it later if needed
  if (shouldCallRecommenderOffersApi()) {
    pendingRecommendationPromise = queryRecommendedBuyflowOffers(
      eligibleMedTiers
    )
      .then((graphqlResult) => {
        const shouldUseApiResults =
          shouldUseOffersRecommendation() && openToMedRecommendation;

        if (graphqlResult && shouldUseApiResults) {
          // Update the recommendation context with API results
          setPlansInRecommendationContext(
            graphqlResult.recommendedMedTier,
            graphqlResult.eligibleMedTiers,
            flow,
            graphqlResult.recommendationId
          );

          trackEvent("OnRecommendedBuyflowOffersSuccess", {
            eligibleSKUs: eligibleMedTiers,
            recommendedMedTier: graphqlResult.recommendedMedTier,
            eligibleMedTiers: graphqlResult.eligibleMedTiers,
            recommendationId: graphqlResult.recommendationId,
          });
        } else if (graphqlResult) {
          trackEvent("OnRecommendedBuyflowOffersUseDefault", {
            eligibleSKUs: eligibleMedTiers,
            recommendedMedTier: graphqlResult.recommendedMedTier,
            eligibleMedTiers: graphqlResult.eligibleMedTiers,
            recommendationId: graphqlResult.recommendationId,
          });
        } else {
          // Use default values if conditions aren't met or no result
          trackEvent("OnRecommendedBuyflowOffersEmptyResponse", {
            eligibleSKUs: eligibleMedTiers,
          });
        }
        return graphqlResult;
      })
      .catch((error) => {
        captureException(error, "loadRecommendedMedPlans");
        trackEvent("OnRecommendedBuyflowOffersError", {
          error: error?.message,
          eligibleSKUs: eligibleMedTiers,
        });
        return null;
      })
      .finally(() => {
        // Clear the promise reference after completion
        pendingRecommendationPromise = null;
      });
  }
};

export const loadEligibleSaleItems: ActionHandler = async ({ store }) => {
  try {
    const eligibleOffers = await queryBuyflowEligibleOffers();
    const eligibleSaleItems: SaleItemInfo[] = [];
    eligibleOffers?.forEach((offer) => {
      const medSku = getMedSKUByProductName(offer.productName);
      if (medSku) {
        eligibleSaleItems.push({
          medSku,
          saleItemId: offer.saleItemId,
          trialSaleItemId: offer.trialSaleItemIds[0],
          planName: offer.productName,
        });
      }
    });

    store.dispatch(
      setRecommendationContext({
        eligibleSaleItems,
      })
    );
  } catch (error) {
    store.dispatch(setRecommendationContext({ eligibleSaleItems: [] }));
    captureException(error, "loadEligibleSaleItems");
    trackEvent("OnLoadEligibleSaleItemsError", {
      error: error?.message,
    });
  }
};

/**
 * Waits for the pending recommendation query to complete (if any).
 * This should be called before pages that require the recommendation data,
 * such as plan reveal pages.
 */
export const waitForRecommendedMedPlans: ActionHandler = async () => {
  if (pendingRecommendationPromise) {
    // Wait for the pending query to complete
    await pendingRecommendationPromise;
  }
  // If there's no pending promise, this action does nothing and returns immediately
};
